import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Enhanced interfaces that work with existing backend data
export interface LearningAnalytics {
  user_identifier: string;
  analysis_period_days: number;
  recent_activity: RecentActivity[];
  topic_analytics: TopicAnalytics[];
  learning_velocity: VelocityData[];
  game_trends: GameTrends[];
  streak_data: StreakData[];
  generated_at: string;
}

// Existing backend data structures
export interface ExistingProgressData {
  completedTopics: string[];
  lastUpdated: string;
  lastCompletedTopic: {
    topicKey: string;
    topicName: string;
    topicType: 'ai-generated' | 'pre-generated';
    language: string;
    finalScore: number;
    wordsFound: number;
    totalWords: number;
    completedAt: string;
  };
}

export interface ExistingScoreData {
  id: number;
  user_identifier: string;
  score: number;
  level: number;
  quiz_category: string;
  topic_key: string;
  time_submitted: string;
}

export interface RecentActivity {
  quiz_category: string;
  topic_key: string;
  score: number;
  level: number;
  activity_date: string;
  daily_count: number;
  avg_score: number;
}

export interface TopicAnalytics {
  quiz_category: string;
  topic_key: string;
  attempts: number;
  avg_score: number;
  best_score: number;
  lowest_score: number;
  score_consistency: number;
  last_attempt: string;
}

export interface VelocityData {
  date: string;
  games_played: number;
  daily_avg_score: number;
}

export interface GameTrends {
  quiz_category: string;
  total_games: number;
  avg_score: number;
  unique_topics: number;
  last_played: string;
  content_type: 'AI-Generated' | 'Pre-Generated' | 'Unknown';
  content_count: number;
}

export interface StreakData {
  activity_date: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private apiUrl = environment.production 
    ? 'https://molhy.com/wp-json/molhy/v1' 
    : 'https://molhy.com/wp-json/molhy/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get comprehensive learning analytics using existing endpoints
   */
  getLearningAnalytics(userIdentifier: string, days: number = 30): Observable<LearningAnalytics> {
    // Use existing endpoints to gather data
    const gameTypes = ['SearchWord', 'BlankWord', 'ErrorWord'];
    
    const requests = gameTypes.map(gameType => 
      this.http.get<ExistingScoreData[]>(`${this.apiUrl}/get-quiz-scores`, {
        params: { user_identifier: userIdentifier, quiz_category: gameType }
      })
    );

    const progressRequests = gameTypes.map(gameType =>
      this.http.get<{progress_state: string}>(`${this.apiUrl}/get-quiz-progress`, {
        params: { user_identifier: userIdentifier, quiz_category: gameType }
      })
    );

    return forkJoin([...requests, ...progressRequests]).pipe(
      map(responses => {
        const scoreResponses = responses.slice(0, 3) as ExistingScoreData[][];
        const progressResponses = responses.slice(3) as {progress_state: string}[];
        
        return this.buildAnalyticsFromExistingData(
          userIdentifier, 
          scoreResponses.flat(), 
          progressResponses,
          days
        );
      })
    );
  }

  /**
   * Build analytics from existing backend data structure
   */
  private buildAnalyticsFromExistingData(
    userIdentifier: string,
    scores: ExistingScoreData[],
    progressData: {progress_state: string}[],
    days: number
  ): LearningAnalytics {
    // Parse progress data
    const allProgressData: ExistingProgressData[] = progressData
      .map(p => {
        try {
          return JSON.parse(p.progress_state) as ExistingProgressData;
        } catch {
          return null;
        }
      })
      .filter(p => p !== null) as ExistingProgressData[];

    // Filter recent scores
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    const recentScores = scores.filter(score => 
      new Date(score.time_submitted) >= cutoffDate
    );

    // Build analytics components
    const recentActivity = this.buildRecentActivity(recentScores);
    const topicAnalytics = this.buildTopicAnalytics(scores, allProgressData);
    const learningVelocity = this.buildLearningVelocity(recentScores, days);
    const gameTrends = this.buildGameTrends(scores);
    const streakData = this.buildStreakData(scores);

    return {
      user_identifier: userIdentifier,
      analysis_period_days: days,
      recent_activity: recentActivity,
      topic_analytics: topicAnalytics,
      learning_velocity: learningVelocity,
      game_trends: gameTrends,
      streak_data: streakData,
      generated_at: new Date().toISOString()
    };
  }

  /**
   * Calculate learning velocity score from velocity data
   */
  calculateLearningVelocity(velocityData: VelocityData[]): number {
    if (velocityData.length === 0) return 0;
    
    // Calculate average games per active day
    const totalGames = velocityData.reduce((sum, day) => sum + day.games_played, 0);
    const activeDays = velocityData.length;
    
    return Math.round((totalGames / activeDays) * 10) / 10; // Round to 1 decimal
  }

  /**
   * Calculate performance consistency from topic analytics
   */
  calculateOverallConsistency(topicAnalytics: TopicAnalytics[]): number {
    if (topicAnalytics.length === 0) return 0;
    
    // Use average of individual topic consistencies
    const consistencyScores = topicAnalytics
      .filter(topic => topic.score_consistency !== null)
      .map(topic => {
        const cv = topic.score_consistency / topic.avg_score;
        return Math.max(0, 100 - (cv * 100)); // Convert to consistency percentage
      });
    
    if (consistencyScores.length === 0) return 0;
    
    return Math.round(consistencyScores.reduce((sum, score) => sum + score, 0) / consistencyScores.length);
  }

  /**
   * Calculate streak days from streak data
   */
  calculateCurrentStreak(streakData: StreakData[]): number {
    if (streakData.length === 0) return 0;
    
    const today = new Date();
    let streak = 0;
    let checkDate = new Date(today);
    
    const activityDates = streakData.map(d => d.activity_date);
    
    while (true) {
      const dateString = checkDate.toISOString().split('T')[0];
      
      if (activityDates.includes(dateString)) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        // Allow for today having no activity if streak is 0
        if (dateString === today.toISOString().split('T')[0] && streak === 0) {
          checkDate.setDate(checkDate.getDate() - 1);
          continue;
        }
        break;
      }
    }
    
    return streak;
  }

  /**
   * Get AI engagement ratio from game trends
   */
  getAiEngagementRatio(gameTrends: GameTrends[]): number {
    const totalGames = gameTrends.reduce((sum, trend) => sum + trend.total_games, 0);
    const aiGames = gameTrends
      .filter(trend => trend.content_type === 'AI-Generated')
      .reduce((sum, trend) => sum + trend.content_count, 0);
    
    return totalGames > 0 ? Math.round((aiGames / totalGames) * 100) / 100 : 0;
  }

  /**
   * Identify learning patterns and recommendations
   */
  generateLearningInsights(analytics: LearningAnalytics): string[] {
    const insights: string[] = [];
    
    // Velocity insights
    const velocity = this.calculateLearningVelocity(analytics.learning_velocity);
    if (velocity >= 2) {
      insights.push('🔥 You\'re on fire! Excellent learning velocity.');
    } else if (velocity >= 1) {
      insights.push('📈 Good learning pace. Keep it up!');
    } else if (velocity > 0) {
      insights.push('🌱 Steady progress. Try to play more regularly for better results.');
    }
    
    // AI engagement insights
    const aiRatio = this.getAiEngagementRatio(analytics.game_trends);
    if (aiRatio >= 0.7) {
      insights.push('🤖 AI Challenge Master! You love tackling AI-generated content.');
    } else if (aiRatio >= 0.4) {
      insights.push('⚡ Good balance of AI and pre-made content.');
    } else if (aiRatio > 0) {
      insights.push('🎯 Try more AI-generated topics for personalized challenges!');
    }
    
    // Topic diversity insights
    const uniqueTopics = new Set(analytics.topic_analytics.map(t => t.topic_key)).size;
    if (uniqueTopics >= 10) {
      insights.push('🌟 Amazing topic diversity! You\'re a well-rounded learner.');
    } else if (uniqueTopics >= 5) {
      insights.push('📚 Great variety in topics explored.');
    } else if (uniqueTopics >= 2) {
      insights.push('🔍 Good start! Try exploring more diverse topics.');
    }
    
    // Streak insights
    const streak = this.calculateCurrentStreak(analytics.streak_data);
    if (streak >= 7) {
      insights.push('🏆 Incredible learning streak! You\'re building great habits.');
    } else if (streak >= 3) {
      insights.push('💪 Nice learning streak going!');
    } else if (streak === 0) {
      insights.push('🎯 Start a learning streak today!');
    }
    
    return insights;
  }

  // Helper methods to build analytics from existing data
  private buildRecentActivity(scores: ExistingScoreData[]): RecentActivity[] {
    const activityMap = new Map<string, RecentActivity>();
    
    scores.forEach(score => {
      const date = new Date(score.time_submitted).toISOString().split('T')[0];
      const key = `${score.quiz_category}-${score.topic_key}-${date}`;
      
      if (!activityMap.has(key)) {
        activityMap.set(key, {
          quiz_category: score.quiz_category,
          topic_key: score.topic_key,
          score: score.score,
          level: score.level,
          activity_date: date,
          daily_count: 1,
          avg_score: score.score
        });
      } else {
        const existing = activityMap.get(key)!;
        existing.daily_count++;
        existing.avg_score = (existing.avg_score + score.score) / 2;
      }
    });
    
    return Array.from(activityMap.values())
      .sort((a, b) => new Date(b.activity_date).getTime() - new Date(a.activity_date).getTime());
  }

  private buildTopicAnalytics(scores: ExistingScoreData[], progressData: ExistingProgressData[]): TopicAnalytics[] {
    const topicMap = new Map<string, TopicAnalytics>();
    
    scores.forEach(score => {
      const key = `${score.quiz_category}-${score.topic_key}`;
      
      if (!topicMap.has(key)) {
        topicMap.set(key, {
          quiz_category: score.quiz_category,
          topic_key: score.topic_key,
          attempts: 1,
          avg_score: score.score,
          best_score: score.score,
          lowest_score: score.score,
          score_consistency: 0,
          last_attempt: score.time_submitted
        });
      } else {
        const existing = topicMap.get(key)!;
        existing.attempts++;
        existing.avg_score = (existing.avg_score + score.score) / 2;
        existing.best_score = Math.max(existing.best_score, score.score);
        existing.lowest_score = Math.min(existing.lowest_score, score.score);
        existing.last_attempt = score.time_submitted > existing.last_attempt ? 
          score.time_submitted : existing.last_attempt;
      }
    });
    
    // Calculate consistency scores
    topicMap.forEach((analytics, key) => {
      const topicScores = scores
        .filter(s => `${s.quiz_category}-${s.topic_key}` === key)
        .map(s => s.score);
      
      if (topicScores.length > 1) {
        const mean = topicScores.reduce((sum, s) => sum + s, 0) / topicScores.length;
        const variance = topicScores.reduce((sum, s) => sum + Math.pow(s - mean, 2), 0) / topicScores.length;
        analytics.score_consistency = Math.sqrt(variance);
      }
    });
    
    return Array.from(topicMap.values())
      .sort((a, b) => new Date(b.last_attempt).getTime() - new Date(a.last_attempt).getTime());
  }

  private buildLearningVelocity(scores: ExistingScoreData[], days: number): VelocityData[] {
    const velocityMap = new Map<string, VelocityData>();
    
    scores.forEach(score => {
      const date = new Date(score.time_submitted).toISOString().split('T')[0];
      
      if (!velocityMap.has(date)) {
        velocityMap.set(date, {
          date: date,
          games_played: 1,
          daily_avg_score: score.score
        });
      } else {
        const existing = velocityMap.get(date)!;
        existing.games_played++;
        existing.daily_avg_score = (existing.daily_avg_score + score.score) / 2;
      }
    });
    
    return Array.from(velocityMap.values())
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  private buildGameTrends(scores: ExistingScoreData[]): GameTrends[] {
    const trendMap = new Map<string, GameTrends>();
    
    scores.forEach(score => {
      const contentType: 'AI-Generated' | 'Pre-Generated' | 'Unknown' = 
        score.topic_key?.startsWith('ai-generated') ? 'AI-Generated' :
        score.topic_key?.startsWith('pre-generated') ? 'Pre-Generated' : 'Unknown';
      
      const key = `${score.quiz_category}-${contentType}`;
      
      if (!trendMap.has(key)) {
        trendMap.set(key, {
          quiz_category: score.quiz_category,
          total_games: 1,
          avg_score: score.score,
          unique_topics: new Set([score.topic_key]).size,
          last_played: score.time_submitted,
          content_type: contentType,
          content_count: 1
        });
      } else {
        const existing = trendMap.get(key)!;
        existing.total_games++;
        existing.avg_score = (existing.avg_score + score.score) / 2;
        existing.content_count++;
        existing.last_played = score.time_submitted > existing.last_played ? 
          score.time_submitted : existing.last_played;
      }
    });
    
    return Array.from(trendMap.values())
      .sort((a, b) => b.total_games - a.total_games);
  }

  private buildStreakData(scores: ExistingScoreData[]): StreakData[] {
    const uniqueDates = new Set<string>();
    
    scores.forEach(score => {
      const date = new Date(score.time_submitted).toISOString().split('T')[0];
      uniqueDates.add(date);
    });
    
    return Array.from(uniqueDates)
      .map(date => ({ activity_date: date }))
      .sort((a, b) => new Date(b.activity_date).getTime() - new Date(a.activity_date).getTime());
  }
}